// ─── 🗂️ Le sezioni della scheda del socio (27/10/2026) ───────────────────────
//
// Mockup «Scheda del socio a sezioni», A: 🗣️ *«così tutta verticale è un po' confusionaria. Forse dobbiamo fare delle
// sezioni che si possono cliccare in orizzontale»*. Sotto il nome una riga con le cose urgenti (tessera, livello,
// saldo, bot), che si vede da ogni sezione; poi le sezioni da toccare. I riquadri sono quelli di prima: cambia solo
// dove stanno. La scheda si riapre sull'ultima sezione guardata.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  formatCents,
  livelloInParole,
  type InvitoAlBot,
  type Socio,
  type TesseraDelSocio,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { usaBorsellino, usaScalaDeiLivelli } from '../../features/soci/dati.js';

export const SEZIONI = [
  { chiave: 'dati', titolo: 'Dati' },
  { chiave: 'livello', titolo: 'Livello' },
  { chiave: 'bot', titolo: 'Bot' },
  { chiave: 'soldi', titolo: 'Tessera e soldi' },
  { chiave: 'attivita', titolo: 'Attività' },
] as const;
export type Sezione = (typeof SEZIONI)[number]['chiave'];

const RICORDO = 'scheda-socio-sezione';

/** L'ultima sezione guardata, fra quelle che ci sono. Senza la memoria del browser si parte da «Dati». */
export function usaSezione(ci: readonly Sezione[]): [Sezione, (s: Sezione) => void] {
  const [scelta, setScelta] = useState<Sezione>(() => {
    try {
      return (localStorage.getItem(RICORDO) as Sezione | null) ?? 'dati';
    } catch {
      return 'dati';
    }
  });
  const scegli = (s: Sezione) => {
    setScelta(s);
    try {
      localStorage.setItem(RICORDO, s);
    } catch {
      // La memoria del browser può mancare: la sezione vale per questa scheda.
    }
  };
  return [ci.includes(scelta) ? scelta : (ci[0] ?? 'dati'), scegli];
}

export function Linguette({
  ci,
  scelta,
  scegli,
}: {
  ci: readonly Sezione[];
  scelta: Sezione;
  scegli: (s: Sezione) => void;
}): React.JSX.Element {
  return (
    <div className="sezioni-socio" role="tablist" aria-label="Sezioni della scheda">
      {SEZIONI.filter((s) => ci.includes(s.chiave)).map((s) => (
        <button
          key={s.chiave}
          type="button"
          role="tab"
          id={`sezione-${s.chiave}`}
          aria-selected={scelta === s.chiave}
          aria-controls="sezione-aperta"
          onClick={() => scegli(s.chiave)}
        >
          {s.titolo}
        </button>
      ))}
    </div>
  );
}

const giorno = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('it-IT');

/**
 * La riga sotto il nome. Ogni pezzo c'è solo se il ruolo lo può vedere e il gestionale l'ha detto: un pezzo che non
 * arriva si salta, il riquadro della sua sezione dice il guasto.
 */
export function RigaDelSocio({
  socio,
  vedeAnagrafica,
  vedeBorsellino,
}: {
  socio: Socio;
  vedeAnagrafica: boolean;
  vedeBorsellino: boolean;
}): React.JSX.Element {
  const fasce = usaScalaDeiLivelli().data?.fasce ?? [];
  const tessera = useQuery({
    queryKey: ['tessera-socio', socio.id],
    queryFn: () => chiama<TesseraDelSocio>('GET', `/members/${socio.id}/tessera`),
    enabled: vedeAnagrafica,
  }).data;
  // La stessa domanda di `usaInvitoBot`, accesa solo per chi vede l'anagrafica.
  const bot = useQuery({
    queryKey: ['invito-bot', socio.id],
    queryFn: () => chiama<InvitoAlBot>('GET', `/members/${socio.id}/bot-invite`),
    enabled: vedeAnagrafica,
  }).data;
  const borsellino = usaBorsellino(vedeBorsellino ? socio.id : null).data;

  const pezzi: React.JSX.Element[] = [];
  if (vedeAnagrafica && tessera) {
    pezzi.push(
      <span key="t">
        Tessera:{' '}
        {tessera.stato === 'attiva' && tessera.scade ? (
          <b>fino al {giorno(tessera.scade)}</b>
        ) : tessera.stato === 'scaduta' ? (
          <b className="rosso">scaduta</b>
        ) : (
          <b className="rosso">da fare</b>
        )}
      </span>,
    );
  }
  pezzi.push(
    <span key="l">
      Livello: <b>{livelloInParole(fasce, socio.level)}</b>
    </span>,
  );
  if (borsellino) {
    pezzi.push(
      <span key="s">
        Saldo: <b>{formatCents(borsellino.balanceCents)}</b>
      </span>,
    );
  }
  if (vedeAnagrafica && bot) {
    pezzi.push(
      <span key="b">
        Bot: <b>{bot.inBot ? 'sì' : 'no'}</b>
      </span>,
    );
  }
  return (
    <p className="riga-del-socio piccolo tenue">
      {pezzi.flatMap((p, i) => (i === 0 ? [p] : [<span key={`p${i}`}> · </span>, p]))}
    </p>
  );
}

/** Nella sezione «Livello», il livello della scheda: si cambia da «Modifica l’anagrafica», in «Dati». */
export function LivelloDellaScheda({
  socio,
  modificabile,
}: {
  socio: Socio;
  modificabile: boolean;
}): React.JSX.Element {
  const fasce = usaScalaDeiLivelli().data?.fasce ?? [];
  return (
    <section className="riquadro">
      <h3>Livello</h3>
      <p>
        <strong>{livelloInParole(fasce, socio.level)}</strong>
      </p>
      {modificabile && (
        <p className="piccolo tenue">Si cambia da «Modifica l’anagrafica», nella sezione Dati.</p>
      )}
    </section>
  );
}
